// Core
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

// Libraries
import dayjs from 'dayjs';
import { isNil } from 'lodash';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import {
  AxisRenderer,
  ILineSeriesSettings,
} from '@amcharts/amcharts5/xy';

// Types
import { Theme } from '../../../types/theme';
import {
  LengthType,
  MeasurementType,
  PressureType,
  TemperatureType,
} from '../../../types/global';

// Plugins
import {
  convertLength,
  convertPressure,
  convertTemperature,
  formatNumber,
} from '../../../plugins/general';

// Store
import {
  useMeasurementStore,
} from '../../../store/measurement.store';

// Component
import { Element } from './LineChart.style';
import {
  AdapterTarget,
  BaseIntervalResult,
  ChartConfiguration,
  ChartDataScalar,
  DateFormatKey,
  DateFormatSet,
  IChartDataRecord,
  LanguageFormatSets,
  LineChartProps,
  Series,
  StatusMappingResult,
} from './LineChart.types';

/* ************************** RESPONSIVE CHART RULES ************************** */

// Breakpoint to apply responsive rules (below lg = 992px)
const RESPONSIVE_CHART_BREAKPOINT: number = 992;

// Responsive values for mobile/tablet screens
const CHART_MOBILE_RULES = {
  xAxis: { fontSize: 10, paddingTop: 10 },
  yAxis: { fontSize: 10 },
  unit: { fontSize: 10, height: 30 },
  legend: { fontSize: 10, fontWeight: 'normal', paddingLeft: 8 },
  chart: { paddingTop: 35, paddingBottom: -5 },
  minGridDistance: { x: 80, y: 40 },
} as const;

// Original desktop values (restored when resizing back up)
const CHART_DESKTOP_RULES = {
  xAxis: { fontSize: 14, paddingTop: 10 },
  yAxis: { fontSize: 14 },
  unit: { fontSize: 14, height: 100 },
  legend: { fontSize: 14, fontWeight: 'bold', paddingLeft: 20 },
  chart: { paddingTop: 40, paddingBottom: -5 },
  minGridDistance: { x: 120, y: 50 },
} as const;

function LineChart({
  activeBullet,
  className,
  compactLegend = false,
  data,
  grid = false,
  id,
  series,
  testId,
}: LineChartProps): JSX.Element {
  // Dependencies
  const { i18n } = useTranslation();
  const theme: Theme = useTheme();

  // Store states for conversions
  const pressureUnit = useMeasurementStore((state) => state.pressure);
  const temperatureUnit = useMeasurementStore((state) => state.temperature);
  const lengthUnit = useMeasurementStore((state) => state.length);

  /* ************************** THEME ************************** */
  /**
   * Memoized chart configuration based on the current theme.
   *
   * @returns {ChartConfiguration} The complete chart configuration object
   */
  const chartConfiguration = useMemo((): ChartConfiguration => ({
    colors: {
      fillColor: theme.color.lineChart.colorFill,
      statusColor: theme.color.lineChart.colorStatus,
      yAxisColor: theme.color.lineChart.yAxisColor,
      lineSeriesColor: {
        purple: theme.color.lineChart.seriesColorPurple,
        yellow: theme.color.lineChart.seriesColorYellow,
        lightOrange: theme.color.lineChart.seriesColorLightOrange,
        green: theme.color.lineChart.seriesColorGreen,
        orange: theme.color.lineChart.seriesColorOrange,
        blue: theme.color.lineChart.seriesColorBlue,
        pink: theme.color.lineChart.seriesColorPink,
        lightGreen: theme.color.lineChart.seriesColorLightGreen,
        lightPink: theme.color.lineChart.seriesColorLightPink,
        lightBlue: theme.color.lineChart.seriesColorLightBlue,
      },
    },
    fonts: {
      regular: theme.font.regular,
    },
  }), [theme]);

  /* ************************** CONVERSION UNIT ************************** */

  /**
   * Converts a temperature value using the selected unit.
   *
   * @param value - The temperature value.
   * @returns The converted temperature.
  */
  const convertTemperatureValue = useCallback(
    (value: number): number | null | undefined => {
      const result = convertTemperature(
        value,
        temperatureUnit,
      );
      return result.value;
    },
    [temperatureUnit],
  );

  /**
   * Converts a pressure value using the selected unit.
   *
   * @param value - The pressure value.
   * @returns The converted pressure.
  */
  const convertPressureValue = useCallback(
    (value: number): number | null | undefined => (
      convertPressure(
        value,
        pressureUnit,
      ).value
    ),
    [pressureUnit],
  );

  /**
   * Converts a length value using the selected unit.
   *
   * @param value - The length value.
   * @returns The converted length.
  */
  const convertLengthValue = useCallback(
    (value: number): number | null | undefined => (
      convertLength(
        value,
        lengthUnit,
      )
    ).value,
    [lengthUnit],
  );

  /**
   * Returns the pressure unit label based on the selected pressure type.
   *
   * @returns The pressure unit label.
  */
  const getPressureUnitLabel = useCallback((): string => {
    switch (pressureUnit) {
      case PressureType.bar:
        return PressureType.bar;
      case PressureType.PSI:
        return PressureType.PSI;
      case PressureType.MPa:
        return PressureType.MPa;
      default:
        return '';
    }
  }, [pressureUnit]);

  /**
   * Converts a numeric value based on the measurement type.
   *
   * @param value - The value to convert.
   * @param measurementType - The measurement type.
   * @returns The converted value.
  */
  const convertMeasurementValue = useCallback(
    (
      value: number | null | undefined,
      measurementType?: MeasurementType,
    ): number | null | undefined => {
      if (isNil(value)) return value;
      switch (measurementType) {
        case MeasurementType.temperature:
          return convertTemperatureValue(value);
        case MeasurementType.pressure:
          return convertPressureValue(value);
        case MeasurementType.length:
          return convertLengthValue(value);
        default:
          return value;
      }
    },
    [
      convertTemperatureValue,
      convertPressureValue,
      convertLengthValue,
    ],
  );

  /**
   * Returns the label for the converted unit based on the measurement type.
   *
   * @param measurementType - The measurement type.
   * @param unit - The original unit.
   * @returns The converted unit label.
  */
  const getConvertedUnitLabel = useCallback(
    (measurementType?: MeasurementType, unit?: string): string => {
      if (!measurementType) return unit || '';
      switch (measurementType) {
        case MeasurementType.temperature:
          return temperatureUnit === TemperatureType.f ? '°F' : '°C';
        case MeasurementType.pressure:
          return getPressureUnitLabel();
        case MeasurementType.length:
          return lengthUnit === LengthType.inch ? 'in' : 'mm';
        default:
          return unit || '';
      }
    },
    [
      temperatureUnit,
      lengthUnit,
      getPressureUnitLabel,
    ],
  );

  /* ***********************************************************************************************
  ******************************************* METHODS **********************************************
  *********************************************************************************************** */

  // Maximum number of data points to display in the chart
  const MAX_DATA_POINTS: number = 600;

  // Reference chart
  const chartRef: React.MutableRefObject<am5xy.XYChart | null> = useRef<am5xy.XYChart | null>(null);

  // Reference root
  const rootRef: React.MutableRefObject<am5.Root | undefined> = useRef(undefined);

  // Reference xAxis (DateAxis)
  const xAxisRef: React.MutableRefObject<am5xy.DateAxis<AxisRenderer> | null> = useRef(null);

  // Store current date format to avoid unnecessary updates
  const currentDateFormatRef: React.MutableRefObject<string | null> = useRef(null);

  const containerRef = useRef<HTMLDivElement | null>(null);

  // Default series colors
  const defaultSeriesColor: string[] = Object.values(chartConfiguration.colors.lineSeriesColor).map(
    (color: string) => color,
  );

  /**
   * Creates a mapping of status values to their corresponding indices.
   *
   * @param statusArray - Array of status strings to be mapped
   * @returns {StatusMappingResult} Object mapping status strings to their array indices
   */
  const mapStatusArrayToIndex = useCallback((statusArray: string[]): StatusMappingResult => {
    const mapping: StatusMappingResult = {};
    statusArray.forEach((status: string, index: number) => {
      mapping[status] = index;
    });
    return mapping;
  }, []);

  /**
   * Calculates the appropriate autoGapCount based on the baseInterval.
   * This determines when the chart should automatically create gaps in the data.
   * Formula: gap occurs when (distance > baseInterval × autoGapCount)
   *
   * @param baseInterval - The base interval configuration
   * @returns {number} The calculated autoGapCount value
   */
  const calculateAutoGapCount = useCallback((baseInterval: BaseIntervalResult): number => {
    const { timeUnit, count } = baseInterval;

    // Define reasonable gap thresholds for each time unit
    // The goal is to create a gap after a reasonable period based on the time unit
    // Logic: millisecond (~1min gap), second (~1min gap), minute (~25min gap),
    // hour (~12-24h gap), day (~5-7d gap), week (~4-6w gap), month (~3-6mo gap), year (~2-3y gap)
    switch (timeUnit) {
      case 'millisecond':
        // Gap after ~60 seconds (60000ms)
        return Math.max(10, Math.round(60000 / count));
      case 'second':
        // Gap after ~60 seconds
        return Math.max(5, Math.round(60 / count));
      case 'minute':
        // Gap after ~25 minutes
        return Math.max(1, Math.round(25 / count));
      case 'hour':
        // Gap after ~18 hours (proportional to minute: 25min * 60min/hour ≈ 18-24h)
        return Math.max(2, Math.round(18 / count));
      case 'day':
        // Gap after ~5 days (proportional: roughly a work week)
        return Math.max(2, Math.round(5 / count));
      case 'week':
        // Gap after ~5 weeks (roughly a month+)
        return Math.max(2, Math.round(5 / count));
      case 'month':
        // Gap after ~4 months (roughly a third of a year)
        return Math.max(2, Math.round(4 / count));
      case 'year':
        // Gap after ~2 years
        return Math.max(1, Math.round(2 / count));
      default:
        return 60;
    }
  }, []);

  /**
   * Gets the appropriate date format key based on the time period.
   *
   * @param totalPeriodMs - Total time period in milliseconds
   * @returns {DateFormatKey} The appropriate format key
   */
  const getFormatKeyByPeriod = useCallback((totalPeriodMs: number): DateFormatKey => {
    let result: DateFormatKey = 'yearOnly';

    const minute: number = 60 * 1000;
    const hour: number = 60 * minute;
    const day: number = 24 * hour;
    const month: number = 30 * day;
    const year: number = 365 * day;

    if (totalPeriodMs <= 2 * hour) {
      result = totalPeriodMs <= 10 * minute ? 'seconds' : 'minutes';
    } else if (totalPeriodMs <= 2 * day) {
      result = 'dateTime';
    } else if (totalPeriodMs <= 2 * month) {
      result = 'dateOnly';
    } else if (totalPeriodMs <= year) {
      result = 'dateMonth';
    } else if (totalPeriodMs <= 5 * year) {
      result = 'monthYear';
    }

    return result;
  }, []);

  /**
   * Determines the appropriate date format based on the total time period of the data and language.
   * This provides better user experience by showing relevant time information in locale.
   *
   * @param items - Array of chart data records
   * @param dateField - The field name containing timestamp data
   * @returns {string} The appropriate date format string
   */
  const determineDateFormatByPeriod = useCallback((
    items: IChartDataRecord[],
    dateField: string,
  ): string => {
    const isPortuguese: boolean = i18n.language === 'pt_BR';

    // Define format sets based on language
    const formatSets: LanguageFormatSets = {
      portuguese: {
        seconds: 'HH:mm:ss',
        minutes: 'HH:mm',
        dateTime: 'DD/MM HH:mm',
        dateOnly: 'DD/MM',
        dateMonth: 'DD/MMM',
        monthYear: 'MMM/YY',
        yearOnly: 'YYYY',
        default: 'DD/MM/YY HH:mm',
      },
      english: {
        seconds: 'hh:mm:ss A',
        minutes: 'hh:mm A',
        dateTime: 'MM/DD hh:mm A',
        dateOnly: 'MM/DD',
        dateMonth: 'MMM/DD',
        monthYear: 'MMM/YY',
        yearOnly: 'YYYY',
        default: 'MM/DD/YY hh:mm A',
      },
    };

    const formats: DateFormatSet = isPortuguese ? formatSets.portuguese : formatSets.english;
    let dateFormat: string = formats.default; // Use typed default format

    if (Array.isArray(items) && items.length >= 2) {
      const timestamps: number[] = items
        .map((item: IChartDataRecord) => item?.[dateField])
        .filter((v: ChartDataScalar): v is number => typeof v === 'number')
        .sort((a: number, b: number) => a - b);

      if (timestamps.length >= 2) {
        // Calculate total period (last timestamp - first timestamp)
        const totalPeriodMs: number = timestamps[timestamps.length - 1] - timestamps[0];

        // Get the appropriate format key and corresponding format
        const formatKey: DateFormatKey = getFormatKeyByPeriod(totalPeriodMs);
        dateFormat = formats[formatKey];
      }
    }

    return dateFormat;
  }, [i18n.language, getFormatKeyByPeriod]);

  /**
   * Updates the date format adapter only when the format changes to avoid repeated updates.
   *
   * @param newDateFormat - The new date format to apply
   */
  const updateDateFormatAdapter = useCallback((newDateFormat: string): void => {
    if (xAxisRef.current && currentDateFormatRef.current !== newDateFormat) {
      currentDateFormatRef.current = newDateFormat;

      // Remove existing text adapter if exists and add the new one
      const labelsTemplate = xAxisRef.current.get('renderer').labels.template;
      labelsTemplate.adapters.remove('text');

      labelsTemplate.adapters.add('text', (
        text: string | undefined,
        target: am5xy.AxisLabel,
      ): string | undefined => {
        let formattedText: string | undefined = text;

        if (target && typeof target === 'object' && 'dataItem' in target) {
          const adapterTarget: AdapterTarget = target;
          if (adapterTarget.dataItem) {
            const timestamp: number | undefined = adapterTarget.dataItem.get('value');
            if (typeof timestamp === 'number') {
              const dayObj: dayjs.Dayjs = dayjs(timestamp);
              formattedText = dayObj.format(newDateFormat);
            }
          }
        }

        return formattedText;
      });
    }
  }, []);

  /**
   * Calculates a robust base interval based on the total time period and data density.
   * Uses a hybrid approach: considers both the total period and average interval between points
   * to determine the most appropriate timeUnit and count for AmCharts DateAxis.
   *
   * @param items - Array of chart data records
   * @param dateField - The field name containing timestamp data
   * @returns {BaseIntervalResult} Object containing the calculated time unit and count
   */
  const calculateBaseInterval = useCallback((
    items: IChartDataRecord[],
    dateField: string,
  ): BaseIntervalResult => {
    if (!Array.isArray(items) || items.length < 2) {
      return { timeUnit: 'hour', count: 1 };
    }

    const timestamps: number[] = items
      .map((item: IChartDataRecord) => item?.[dateField] as number)
      .filter(Boolean)
      .sort((a: number, b: number) => a - b);

    if (timestamps.length < 2) {
      return { timeUnit: 'hour', count: 1 };
    }

    // Constants of time in milliseconds
    const second: number = 1000;
    const minute: number = 60 * second;
    const hour: number = 60 * minute;
    const day: number = 24 * hour;
    const week: number = 7 * day;
    const approxMonth: number = 30 * day;
    const approxYear: number = 365 * day;

    // Calculates the intervals between consecutive points
    const intervals: number[] = [];
    for (let i = 1; i < timestamps.length; i += 1) {
      intervals.push(timestamps[i] - timestamps[i - 1]);
    }

    // Sorts the intervals
    const sortedIntervals: number[] = [...intervals].sort((a: number, b: number) => a - b);
    const percentile10Index: number = Math.floor(sortedIntervals.length * 0.1);
    const baseIntervalMs: number = sortedIntervals[Math.max(0, percentile10Index)];

    // Calculates the total period for the specific case of 7 days
    const firstTimestamp: number = timestamps[0];
    const lastTimestamp: number = timestamps[timestamps.length - 1];
    const totalPeriodMs: number = lastTimestamp - firstTimestamp;

    if (totalPeriodMs >= 5 * day && totalPeriodMs <= 8 * day) {
      if (baseIntervalMs >= 15 * minute && baseIntervalMs < 30 * minute) {
        return { timeUnit: 'minute', count: 20 };
      }
      return { timeUnit: 'minute', count: 20 };
    }

    if (baseIntervalMs < second) {
      return { timeUnit: 'millisecond', count: Math.max(1, Math.ceil(baseIntervalMs)) };
    }

    if (baseIntervalMs < minute) {
      return { timeUnit: 'second', count: Math.max(1, Math.ceil(baseIntervalMs / second)) };
    }

    if (baseIntervalMs < hour) {
      return { timeUnit: 'minute', count: Math.max(1, Math.ceil(baseIntervalMs / minute)) };
    }

    if (baseIntervalMs < day) {
      return { timeUnit: 'hour', count: Math.max(1, Math.ceil(baseIntervalMs / hour)) };
    }

    if (baseIntervalMs < week) {
      return { timeUnit: 'day', count: Math.max(1, Math.ceil(baseIntervalMs / day)) };
    }

    if (baseIntervalMs < approxMonth) {
      return { timeUnit: 'week', count: Math.max(1, Math.ceil(baseIntervalMs / week)) };
    }

    if (baseIntervalMs < approxYear) {
      return { timeUnit: 'month', count: Math.max(1, Math.ceil(baseIntervalMs / approxMonth)) };
    }

    return { timeUnit: 'year', count: Math.max(1, Math.ceil(baseIntervalMs / approxYear)) };
  }, []);

  /**
   * Initializes the main XYChart instance.
   *
   * @param root - amCharts Root instance.
   * @returns The configured am5xy.XYChart.
   */
  const createChart = React.useCallback((
    root: am5.Root,
    paddingLeft: number,
    paddingRight: number,
  ): am5xy.XYChart => {
    const chart: am5xy.XYChart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        pinchZoomX: false,
        layout: root.verticalLayout,
        paddingTop: 40,
        paddingRight,
        paddingLeft,
        paddingBottom: -5,
      }),
    );
    chart.zoomOutButton.set('forceHidden', true);
    chart.plotContainer.setAll({ interactive: true, wheelable: false });
    return chart;
  }, []);

  /**
   * Calculates the horizontal padding required to keep all Y axes within the viewport.
   *
   * @returns Padding values for left and right sides of the chart
   */
  const calculateAxesPadding = useCallback((): { paddingLeft: number; paddingRight: number } => {
    // amCharts 5 automatically shrinks the plotContainer for each Y axis added,
    // so no extra padding is needed per axis — the library handles axis spacing natively.
    // A fixed base of 10px on the left prevents the unit label text from being clipped
    // by the SVG boundary on narrow axes (e.g. "25" close to the overflow margin).
    const BASE_LEFT_PADDING: number = 10;

    return {
      paddingLeft: BASE_LEFT_PADDING,
      paddingRight: 0,
    };
  }, []);

  /**
   * Creates and configures the main X axis (DateAxis).
   * Calculates base interval, styles labels and attaches the custom date formatter adapter.
   *
   * @param root - amCharts Root instance
   * @param chart - The XYChart instance where the axis will be attached
   * @param dataItems - Raw data array used to infer base interval
   * @param seriesConfig - Series configuration array
   * @returns {am5xy.DateAxis<AxisRenderer>} The configured DateAxis instance
   */
  const createXAxis = React.useCallback((
    root: am5.Root,
    chart: am5xy.XYChart,
    dataItems: IChartDataRecord[],
    seriesConfig: Series[],
  ): am5xy.DateAxis<AxisRenderer> => {
    const dateFieldName: string = seriesConfig?.[0]?.date || 'timestamp';
    const baseIntervalUnit: BaseIntervalResult = calculateBaseInterval(
      Array.isArray(dataItems) ? dataItems : [],
      dateFieldName,
    );

    // Get the appropriate date format based on the total period
    const dateFormat: string = determineDateFormatByPeriod(
      Array.isArray(dataItems) ? dataItems : [],
      dateFieldName,
    );

    const xAxis: am5xy.DateAxis<AxisRenderer> = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: baseIntervalUnit,
        groupData: false,
        extraMax: 0.01,
        renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 120 }),
      }),
    );

    xAxis.get('renderer').labels.template.setAll({
      fill: am5.color(chartConfiguration.colors.statusColor),
      location: 1,
      paddingTop: 10,
      fontSize: 14,
    });

    xAxis.get('renderer').labels.template.adapters.add('text', (
      text: string | undefined,
      target: am5xy.AxisLabel,
    ): string | undefined => {
      let formattedText: string | undefined = text;

      // Safe type checking instead of type casting
      if (target && typeof target === 'object' && 'dataItem' in target) {
        const adapterTarget: AdapterTarget = target;
        if (adapterTarget.dataItem) {
          const timestamp: number | undefined = adapterTarget.dataItem.get('value');
          if (typeof timestamp === 'number') {
            const dayObj: dayjs.Dayjs = dayjs(timestamp);
            // Use the period-based format instead of baseInterval-based format
            formattedText = dayObj.format(dateFormat);
          }
        }
      }

      return formattedText;
    });

    return xAxis;
  }, [
    chartConfiguration.colors.statusColor,
    determineDateFormatByPeriod,
    calculateBaseInterval,
  ]);

  /**
   * Filters series configuration to get unique units only.
   *
   * @param seriesConfig - Array of series configurations
   * @returns {Series[]} Array of series with unique units
   */
  const getUniqueSeriesByUnit = useCallback((seriesConfig: Series[]): Series[] => {
    const filterSeriesByUnit: Series[] = seriesConfig
      .filter((s: Series): boolean => s.type !== 'fill' && s.type !== 'status')
      .filter((
        currentSeries: Series,
        index: number,
        self: Series[],
      ): boolean => (
        index === self.findIndex((seriesUnit: Series): boolean => (
          seriesUnit.unit === currentSeries.unit
        ))
      ));

    return filterSeriesByUnit;
  }, []);

  /**
   * Configures a Y axis for special series types (fill and status).
   *
   * @param yAxisInstance - The Y axis instance to configure
   * @param seriesType - The type of series ('fill' or 'status')
   */
  const configureSpecialYAxis = useCallback((
    yAxisInstance: am5xy.ValueAxis<AxisRenderer>,
    seriesType: string,
  ): void => {
    if (seriesType === 'fill' || seriesType === 'status') {
      yAxisInstance.setAll({ strictMinMax: true, min: 0, max: 1 });
      if (seriesType === 'fill') {
        yAxisInstance.get('renderer').labels.template.setAll({
          isMeasured: false,
          dx: 9999,
        });
      }
    }
  }, []);

  /**
   * Configures Y axis label formatting and number display.
   *
   * @param yAxisInstance - The Y axis instance to configure
   * @param decimals - Number of decimal places to show
   */
  const configureYAxisLabels = useCallback((
    yAxisInstance: am5xy.ValueAxis<AxisRenderer>,
    decimals: number,
  ): void => {
    yAxisInstance.get('renderer').labels.template.set('text', '{value}');
    yAxisInstance.get('renderer').labels.template.adapters.add('text', (
      text: string | undefined,
      target: am5xy.AxisLabel,
    ): string | undefined => {
      let result: string | undefined = text;

      // Safe type checking instead of type casting
      if (target && typeof target === 'object' && 'dataItem' in target) {
        const adapterTarget = target as AdapterTarget;
        const value: number | undefined = adapterTarget.dataItem?.get('value');
        if (value !== undefined) {
          // Axis labels use maximumFractionDigits only (no minimumFractionDigits)
          // so large values like 3500 show as "3,500" instead of "3,500.00"
          const axisDecimals: number = Math.abs(value) >= 100 ? 0 : decimals;
          result = formatNumber(value, axisDecimals);
        }
      }

      return result;
    });
  }, []);

  /**
   * Calculates the minimum and maximum values from data for a specific field.
   *
   * @param chartData - Array of chart data records
   * @param field - Field name to analyze
   * @returns {object} Object containing min and max values
   */
  const calculateDataMinMax = useCallback((
    chartData: IChartDataRecord[],
    field: string,
  ): { min: number | undefined; max: number | undefined } => {
    const values = chartData
      .map((item: IChartDataRecord) => item[field])
      .filter((value: ChartDataScalar) => typeof value === 'number' && !Number.isNaN(value))
      .map((value: ChartDataScalar) => value as number);

    if (values.length === 0) {
      return { min: undefined, max: undefined };
    }

    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }, []);

  /**
   * Calculates global min/max for all series that share the same unit and measurement type.
   * This ensures that related series use the same scale.
   *
   * @param processedData - Processed chart data
   * @param seriesConfigs - Array with configurations of series that share the same unit
   * @returns {object} Object containing global min and max
   */
  const calculateGlobalMinMaxForUnit = useCallback((
    processedData: IChartDataRecord[],
    seriesConfigs: Series[],
  ): { min: number | undefined; max: number | undefined } => {
    let globalMin: number | undefined;
    let globalMax: number | undefined;

    seriesConfigs.forEach((config: Series) => {
      // Ignore special series types (fill, status)
      if (config.type === 'fill' || config.type === 'status') {
        return;
      }

      const fieldMinMax: {
        min: number | undefined;
        max: number | undefined;
      } = calculateDataMinMax(processedData, config.field);

      if (fieldMinMax.min !== undefined) {
        globalMin = globalMin === undefined
          ? fieldMinMax.min
          : Math.min(globalMin, fieldMinMax.min);
      }

      if (fieldMinMax.max !== undefined) {
        globalMax = globalMax === undefined
          ? fieldMinMax.max
          : Math.max(globalMax, fieldMinMax.max);
      }
    });

    return { min: globalMin, max: globalMax };
  }, [calculateDataMinMax]);

  /**
   * Calculates a dynamic and proportional margin based on the magnitude of the values.
   * Ensures that the margin works for small, large, zero, or negative values.
   *
   * @param min - Minimum value
   * @param max - Maximum value
   * @param configuredMin - Configured minimum value
   * @param configuredMax - Configured maximum value
   * @returns {number} Calculated margin
   */
  const calculateDynamicMargin = useCallback((
    min: number,
    max: number,
    configuredMin: number,
    configuredMax: number,
  ): number => {
    // Calculate the range of actual data
    const dataRange: number = Math.abs(max - min);

    // Calculate the configured range
    const configuredRange: number = Math.abs(configuredMax - configuredMin);

    // Use the larger range as the base for calculating the margin
    const baseRange: number = Math.max(dataRange, configuredRange);

    // Special case: when min == max (all values are equal)
    if (dataRange === 0) {
      // If the value is zero, use a margin based on the configured range
      if (Math.abs(max) < 0.0001) {
        return configuredRange * 0.15;
      }
      // If not zero, use 15% of the absolute value or configured range
      return Math.max(Math.abs(max) * 0.15, configuredRange * 0.15);
    }

    // Special case: very small values (close to zero)
    if (baseRange < 0.001) {
      return baseRange * 0.2; // 20% for very small values
    }

    // Special case: small values (< 1)
    if (baseRange < 1) {
      return baseRange * 0.15; // 15% for small values
    }

    // Special case: large values (> 1000)
    if (baseRange > 1000) {
      return baseRange * 0.1; // 10% for large values
    }

    // General case: values between 1 and 1000
    return baseRange * 0.12; // 12% for normal values
  }, []);

  /**
   * Creates a unit label for a Y axis.
   *
   * @param root - amCharts Root instance
   * @param unitLabelText - Text to display in the unit label
   * @param currentTheme - Current theme configuration
   * @returns {am5.Label} The created unit label
   */
  const createYAxisUnitLabel = useCallback((
    root: am5.Root,
    unitLabelText: string,
    currentTheme: Theme,
  ): am5.Label => {
    const unitLabel: am5.Label = am5.Label.new(root, {
      rotation: 0,
      text: unitLabelText,
      x: am5.percent(85),
      width: 17,
      height: 100,
      centerX: am5.percent(50),
      centerY: 44,
      fill: am5.color(currentTheme.color.lineChart.yAxisColor),
      fontFamily: currentTheme.font.medium,
      fontSize: 14,
      textAlign: 'center',
      textBaseline: 'middle',
    });

    return unitLabel;
  }, []);

  /**
   * Creates and configures all Y axes based on unique units across the series config.
   * Handles special cases for 'fill' and 'status' series types and attaches a unit label.
   *
   * @param root - amCharts Root instance
   * @param chart - The XYChart instance where axes will be attached
   * @param seriesConfig - Series configuration array
   * @param currentTheme - Design theme
   * @param unitLabelGetter - Function that returns converted unit label
   * @returns {am5xy.ValueAxis<AxisRenderer>[]} Array of configured ValueAxis instances
   */
  const createYAxes = React.useCallback((
    root: am5.Root,
    chart: am5xy.XYChart,
    seriesConfig: Series[],
    currentTheme: Theme,
    unitLabelGetter: (mt?: MeasurementType, unit?: string) => string,
  ): am5xy.ValueAxis<AxisRenderer>[] => {
    const yAxisList: am5xy.ValueAxis<AxisRenderer>[] = [];
    const uniqueSeriesByUnit: Series[] = getUniqueSeriesByUnit(seriesConfig);

    // Phase 1: Create axes for real sensor series.
    // amCharts 5 stacks multiple Y axes on the same side automatically — no dx offset needed.
    // fill/status series are intentionally excluded so they don't skew the offset calculation.
    uniqueSeriesByUnit.forEach((seriesChart: Series, seriesIndex: number): void => {
      // Secondary axes sync their grid lines with the primary axis so that numbers at the same
      // vertical position are visually aligned. strictMinMax is intentionally omitted on sensor
      // axes: the sync mechanism needs to round min/max to produce aligned "nice" grid steps.
      const primaryAxis: am5xy.ValueAxis<AxisRenderer> | undefined = yAxisList[0];
      const syncWithAxis: am5xy.ValueAxis<AxisRenderer> | undefined = (
        seriesIndex > 0 ? primaryAxis : undefined
      );

      const yAxisInstance: am5xy.ValueAxis<AxisRenderer> = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          syncWithAxis,
          renderer: am5xy.AxisRendererY.new(root, {
            opposite: seriesChart.opposite,
            minGridDistance: 50,
          }),
        }),
      );

      configureSpecialYAxis(yAxisInstance, seriesChart.type);
      configureYAxisLabels(yAxisInstance, seriesChart.decimals || 0);

      yAxisList.push(yAxisInstance);

      const unitLabelText: string = unitLabelGetter(seriesChart.measurementType, seriesChart.unit) || '';

      const unitLabel: am5.Label = createYAxisUnitLabel(root, unitLabelText, currentTheme);
      yAxisInstance.children.unshift(unitLabel);
    });

    // Phase 2: Create hidden axes for fill/status series, appended after all sensor axes.
    // They use dx: 0 and need their own axis (min: 0, max: 1) to render the fill at full height.
    // Appending them last keeps sensor axis indices stable (0..n-1).
    seriesConfig
      .filter((s: Series): boolean => s.type === 'fill' || s.type === 'status')
      .forEach((seriesChart: Series): void => {
        const yAxisInstance: am5xy.ValueAxis<AxisRenderer> = chart.yAxes.push(
          am5xy.ValueAxis.new(root, {
            strictMinMax: true,
            renderer: am5xy.AxisRendererY.new(root, {
              opposite: seriesChart.opposite,
              minGridDistance: 50,
              dx: 0,
            }),
          }),
        );
        configureSpecialYAxis(yAxisInstance, seriesChart.type);
        yAxisList.push(yAxisInstance);
      });

    return yAxisList;
  }, [
    getUniqueSeriesByUnit,
    configureSpecialYAxis,
    configureYAxisLabels,
    createYAxisUnitLabel,
  ]);

  /**
   * Finds the Y axis that matches the series unit.
   *
   * @param yAxes - Array of Y axes
   * @param uniqueSeriesByUnit - Array of unique series by unit
   * @param currentSeries - Current series configuration
   * @returns {am5xy.ValueAxis<AxisRenderer>} The matching Y axis
   */
  const findMatchingYAxis = useCallback((
    yAxes: am5xy.ValueAxis<AxisRenderer>[],
    uniqueSeriesByUnit: Series[],
    currentSeries: Series,
  ): am5xy.ValueAxis<AxisRenderer> => {
    // Fill/status axes are appended after sensor axes (Phase 2 of createYAxes).
    // uniqueSeriesByUnit.length is the offset to the first fill/status axis.
    if (currentSeries.type === 'fill' || currentSeries.type === 'status') {
      return yAxes[uniqueSeriesByUnit.length];
    }
    const axisIndex: number = uniqueSeriesByUnit.findIndex(
      (s: Series): boolean => s.unit === currentSeries.unit,
    );
    return yAxes[axisIndex];
  }, []);

  /**
   * Creates common configuration for all series types.
   *
   * @param root - amCharts Root instance
   * @param currentSeries - Current series configuration
   * @param xAxis - The X axis to use
   * @param yAxis - The Y axis to use
   * @param unitLabelGetter - Function that returns converted unit label
   * @returns {ILineSeriesSettings} Common series configuration
   */
  const createCommonSeriesConfig = useCallback((
    root: am5.Root,
    currentSeries: Series,
    xAxis: am5xy.DateAxis<AxisRenderer>,
    yAxis: am5xy.ValueAxis<AxisRenderer>,
    unitLabelGetter: (mt?: MeasurementType, unit?: string) => string,
  ): ILineSeriesSettings => {
    const convertedUnit: string = unitLabelGetter(
      currentSeries.measurementType,
      currentSeries.unit,
    );
    const seriesName: string = currentSeries.unit
      ? `${currentSeries.name} (${convertedUnit})`
      : currentSeries.name;

    const tooltipDateFormat: string = i18n.language === 'pt_BR'
      ? 'dd/MM/yyyy HH:mm:ss'
      : 'MM/dd/yyyy hh:mm:ss a';

    const commonConfig: ILineSeriesSettings = {
      name: seriesName,
      xAxis,
      yAxis,
      valueYField: currentSeries.field,
      valueXField: currentSeries.date,
      id: currentSeries.field,
      tooltip: currentSeries.type === 'fill'
        ? undefined
        : am5.Tooltip.new(root, {
          pointerOrientation: 'horizontal',
          labelText: `[fontSize:11px]{valueX.formatDate('${tooltipDateFormat}')}[/]\n`
            + '[bold]{name}[/]: {valueY}',
        }),
    };

    return commonConfig;
  }, [i18n.language]);

  /**
   * Creates a line series with smoothing and connection logic.
   *
   * @param root - amCharts Root instance
   * @param config - Common series configuration
   * @param currentSeries - Current series configuration
   * @param chartConfig - Chart visual configuration
   * @param fallbackColors - Array of fallback colors
   * @param seriesIndex - Index of current series
   * @param autoGapCount - The calculated autoGapCount value for gap detection
   * @returns {am5xy.SmoothedXLineSeries} The created line series
   */
  const createLineSeries = useCallback((
    root: am5.Root,
    config: ILineSeriesSettings,
    currentSeries: Series,
    chartConfig: ChartConfiguration,
    fallbackColors: string[],
    seriesIndex: number,
    autoGapCount: number,
  ): am5xy.SmoothedXLineSeries => {
    const seriesChart: am5xy.SmoothedXLineSeries = am5xy.SmoothedXLineSeries.new(root, {
      ...config,
      connect: false,
      minBulletDistance: 0,
      autoGapCount,
      tension: 0.3,
    });

    const color: string = currentSeries.color
      ? (chartConfig.colors.lineSeriesColor[currentSeries.color] || currentSeries.color)
      : fallbackColors[seriesIndex % fallbackColors.length];

    seriesChart.set('stroke', am5.color(color));
    seriesChart.set('fill', am5.color(color));

    return seriesChart;
  }, []);

  /**
   * Creates a step line series for fill or status types.
   *
   * @param root - amCharts Root instance
   * @param config - Common series configuration
   * @param currentSeries - Current series configuration
   * @param chartConfig - Chart visual configuration
   * @param yAxis - The Y axis associated with this series
   * @returns {am5xy.StepLineSeries} The created step line series
   */
  const createStepLineSeries = useCallback((
    root: am5.Root,
    config: ILineSeriesSettings,
    currentSeries: Series,
    chartConfig: ChartConfiguration,
    yAxis: am5xy.ValueAxis<AxisRenderer>,
  ): am5xy.StepLineSeries => {
    const seriesChart: am5xy.StepLineSeries = am5xy.StepLineSeries.new(root, {
      ...config,
      connect: true,
    });

    if (currentSeries.type === 'fill') {
      seriesChart.fills.template.setAll({
        fillOpacity: 0.1,
        visible: true,
        strokeOpacity: 0,
      });
      seriesChart.strokes.template.setAll({ strokeOpacity: 0 });

      const fillColor: string = currentSeries.color
        ? chartConfig.colors.lineSeriesColor[currentSeries.color] || currentSeries.color
        : chartConfig.colors.fillColor;

      seriesChart.set('stroke', am5.color(fillColor));
      seriesChart.set('fill', am5.color(fillColor));
    } else if (currentSeries.type === 'status') {
      const statusColor: string = currentSeries.color
        ? (chartConfig.colors.lineSeriesColor[currentSeries.color] || currentSeries.color)
        : chartConfig.colors.statusColor;

      seriesChart.set('stroke', am5.color(statusColor));
      seriesChart.set('fill', am5.color(statusColor));
      yAxis.get('renderer').set('minGridDistance', 1000);
    }

    return seriesChart;
  }, []);

  /**
   * Applies final styling to a series and its Y axis.
   *
   * @param seriesChart - The series to style
   * @param yAxis - The Y axis associated with this series
   * @param currentTheme - Current theme configuration
   */
  const applySeriesStyling = useCallback((
    seriesChart: am5xy.LineSeries,
    yAxis: am5xy.ValueAxis<AxisRenderer>,
    currentTheme: Theme,
  ): void => {
    seriesChart.strokes.template.setAll({ strokeWidth: 1.2 });

    const renderer: am5xy.AxisRendererY = yAxis.get('renderer') as am5xy.AxisRendererY;
    renderer.grid.template.setAll({ visible: false });
    renderer.labels.template.setAll({
      fill: am5.color(currentTheme.color.lineChart.yAxisColor),
      fontFamily: currentTheme.font.regular,
      fontSize: 14,
    });
  }, []);

  /**
   * Configures tooltip formatting for series based on type.
   *
   * @param seriesChart - The series to configure tooltip for
   * @param currentSeries - Current series configuration
   * @param unitLabelGetter - Function that returns converted unit label
   */
  const configureTooltipFormatting = useCallback((
    seriesChart: am5xy.LineSeries,
    currentSeries: Series,
  ): void => {
    const tooltip: am5.Tooltip | undefined = seriesChart.get('tooltip');
    if (!tooltip) return;

    const tooltipDateFormat: string = i18n.language === 'pt_BR'
      ? 'dd/MM/yyyy HH:mm:ss'
      : 'MM/dd/yyyy hh:mm:ss a';
    const timestampLine: string = `[fontSize:11px]{valueX.formatDate('${tooltipDateFormat}')}[/]\n`;

    tooltip.setAll({
      keepTargetHover: true,
      forceHidden: false,
    });

    // Check if series has status configuration
    if (currentSeries.status && tooltip) {
      // Adjust tooltip to show status text instead of value
      tooltip.label.adapters.add('text', (text: string | undefined, target: any): string => {
        const value: number | undefined = target.dataItem?.get('valueY');
        let statusText: string = '';
        if (value !== undefined && currentSeries.status) {
          statusText = value === 0 ? currentSeries.status[0] : currentSeries.status[1];
        }
        return `${timestampLine}[bold fontSize:14px]{name}[/][fontSize:14px]: ${statusText}[/]`;
      });
    } else if (tooltip) {
      // For non-status series, format numbers with decimal places
      tooltip.set(
        'labelText',
        `${timestampLine}[bold fontSize:14px]{name} [/][fontSize:14px]: {valueY}[/]`,
      );
      tooltip.label.adapters.add('text', (text: string | undefined, target: any): string | undefined => {
        const value: number | undefined = target.dataItem?.get('valueY');
        return value !== undefined && text
          ? text.replace(
            '{valueY}',
            formatNumber(value, currentSeries.decimals || 0),
          )
          : text;
      });
    }
  }, [i18n.language]);

  /**
   * Creates and configures all data series and their styling.
   * Handles types 'line', 'fill' and 'status', applies colors, tooltips and pushes to the chart.
   *
   * @param root - amCharts Root instance
   * @param chart - The XYChart instance where the series will be attached
   * @param xAxis - The DateAxis instance to bind as X axis
   * @param yAxes - Array of created Y axes
   * @param seriesConfig - Series configuration array
   * @param config - Chart visual configuration (colors, fonts)
   * @param unitLabelGetter - Function that returns converted unit label
   * @param fallbackSeriesColors - List of fallback colors for series
   * @param baseInterval - The calculated base interval for the chart
   */
  const createSeries = React.useCallback((
    root: am5.Root,
    chart: am5xy.XYChart,
    xAxis: am5xy.DateAxis<AxisRenderer>,
    yAxes: am5xy.ValueAxis<AxisRenderer>[],
    seriesConfig: Series[],
    config: ChartConfiguration,
    unitLabelGetter: (mt?: MeasurementType, unit?: string) => string,
    fallbackSeriesColors: string[],
    baseInterval: BaseIntervalResult,
  ): void => {
    const uniqueSeriesByUnit: Series[] = getUniqueSeriesByUnit(seriesConfig);
    const autoGapCount: number = calculateAutoGapCount(baseInterval);

    seriesConfig.forEach((currentSeries: Series, index: number): void => {
      const yAxisWithSameUnit: am5xy.ValueAxis<AxisRenderer> = findMatchingYAxis(
        yAxes,
        uniqueSeriesByUnit,
        currentSeries,
      );

      const commonConfig: ILineSeriesSettings = createCommonSeriesConfig(
        root,
        currentSeries,
        xAxis,
        yAxisWithSameUnit,
        unitLabelGetter,
      );

      let seriesChart: am5xy.LineSeries;

      if (currentSeries.type === 'line') {
        seriesChart = createLineSeries(
          root,
          commonConfig,
          currentSeries,
          config,
          fallbackSeriesColors,
          index,
          autoGapCount,
        );
      } else {
        seriesChart = createStepLineSeries(
          root,
          commonConfig,
          currentSeries,
          config,
          yAxisWithSameUnit,
        );
      }

      applySeriesStyling(seriesChart, yAxisWithSameUnit, theme);

      // Configure tooltip formatting after series creation
      configureTooltipFormatting(seriesChart, currentSeries);

      chart.series.push(seriesChart);
    });
  }, [
    getUniqueSeriesByUnit,
    findMatchingYAxis,
    createCommonSeriesConfig,
    createLineSeries,
    createStepLineSeries,
    applySeriesStyling,
    configureTooltipFormatting,
    calculateAutoGapCount,
    theme,
  ]);

  /**
   * Creates and configures the chart legend.
   *
   * @param root - amCharts Root instance.
   * @param chart - The XYChart instance.
   * @param currentTheme - Design theme.
   */
  const createLegend = React.useCallback((
    root: am5.Root,
    chart: am5xy.XYChart,
    currentTheme: Theme,
    isCompact: boolean,
  ): void => {
    const legend: am5.Legend = chart.children.push(
      isCompact
        ? am5.Legend.new(root, {
          useDefaultMarker: true,
          x: am5.percent(50),
          centerX: am5.percent(50),
          layout: root.gridLayout,
        })
        : am5.Legend.new(root, {
          useDefaultMarker: true,
          x: am5.percent(100),
          centerX: am5.percent(100),
          paddingLeft: 60,
        }),
    );

    legend.markers.template.setAll({
      width: isCompact ? 12 : 10,
      height: isCompact ? 3 : 2,
    });
    legend.data.setAll([...chart.series.values]);
    legend.labels.template.setAll({
      paddingLeft: isCompact ? 6 : 20,
      paddingRight: isCompact ? 12 : 0,
      fontFamily: currentTheme.font.regular,
      fontSize: 14,
      fontWeight: 'bold',
    });
  }, []);

  /**
   * Creates and configures the chart cursor.
   *
   * @param root - amCharts Root instance
   * @param chart - The XYChart instance
   * @param xAxis - The DateAxis instance used by the cursor
   */
  const createCursor = React.useCallback((
    root: am5.Root,
    chart: am5xy.XYChart,
    xAxis: am5xy.DateAxis<AxisRenderer>,
  ): void => {
    const cursor: am5xy.XYCursor = chart.set('cursor', am5xy.XYCursor.new(root, {
      snapToSeries: chart.series.values,
      xAxis,
    }));
    cursor.lineY.set('visible', false);
    cursor.lineX.setAll({
      stroke: am5.color(chartConfiguration.colors.statusColor),
      strokeWidth: 1,
      strokeOpacity: 0.5,
    });
  }, [chartConfiguration.colors.statusColor]);

  // Simplified bullet animation state management
  const bulletAnimationStateRef = useRef<boolean>(Boolean(activeBullet));
  const activeBulletsRef = useRef<Map<string, am5.Circle>>(new Map());

  /**
   * Updates all bullet animations with the given state
   * @param shouldAnimate - Whether bullets should animate
   */
  const updateBulletAnimations = useCallback((shouldAnimate: boolean): void => {
    activeBulletsRef.current.forEach((circleEffect) => {
      if (circleEffect && !circleEffect.isDisposed()) {
        // Stop any existing animations
        circleEffect.animate({
          key: 'radius',
          to: 5,
          duration: 100,
        });
        circleEffect.animate({
          key: 'opacity',
          to: 0.5,
          duration: 100,
        });

        if (shouldAnimate) {
          // Start pulsing animations
          setTimeout(() => {
            if (circleEffect && !circleEffect.isDisposed() && bulletAnimationStateRef.current) {
              circleEffect.animate({
                key: 'radius',
                to: 15,
                duration: 1000,
                easing: am5.ease.out(am5.ease.cubic),
                loops: Infinity,
              });
              circleEffect.animate({
                key: 'opacity',
                from: 0.8,
                to: 0,
                duration: 1000,
                easing: am5.ease.out(am5.ease.cubic),
                loops: Infinity,
              });
            }
          }, 150);
        }
      }
    });
  }, []);

  // Update bullet animation state when activeBullet prop changes
  React.useLayoutEffect(() => {
    const newState: boolean = Boolean(activeBullet);
    const stateChanged: boolean = bulletAnimationStateRef.current !== newState;

    if (stateChanged) {
      bulletAnimationStateRef.current = newState;
      updateBulletAnimations(newState);
    }
  }, [activeBullet, updateBulletAnimations]);

  /**
   * Creates bullet configuration for line series.
   * Creates animated bullets with pulsing effects for active states.
   *
   * @param chart - The XYChart instance containing the series
   */
  const createBullets = React.useCallback((
    chart: am5xy.XYChart,
  ): void => {
    // Clear existing bullets from our tracking
    activeBulletsRef.current.clear();

    chart.series.each((seriesChart: am5xy.XYSeries) => {
      const config: Series | undefined = series.find(
        (s: Series) => s.field === seriesChart.get('valueYField'),
      );

      if (config && config.type === 'line') {
        const seriesId: string = config.field;

        // Clear existing bullet generators to prevent accumulation
        seriesChart.bullets.setAll([]);

        seriesChart.bullets.push((
          customRoot: am5.Root,
          _customSeries: am5.Series,
          dataItem: am5.DataItem<am5xy.IXYSeriesDataItem>,
        ): am5.Bullet | undefined => {
          // Filter the valid data items for the line chart
          const validDataItems:
          am5.DataItem<am5xy.IXYSeriesDataItem>[] = seriesChart.dataItems.filter((
            item: am5.DataItem<am5xy.IXYSeriesDataItem>,
          ) => item.get('valueY') !== null && item.get('valueY') !== undefined);

          // Get the last valid data item
          const lastValidDataItem: am5.DataItem<am5xy.IXYSeriesDataItem> = validDataItems[
            validDataItems.length - 1
          ];

          // Only add bullet if this is the last valid data item
          if (lastValidDataItem && dataItem === lastValidDataItem) {
            // Check and dispose of existing bullet for this series
            const existingCircleEffect: am5.Circle | undefined = activeBulletsRef.current.get(
              seriesId,
            );
            if (existingCircleEffect && !existingCircleEffect.isDisposed()) {
              // Dispose of the parent container which contains both circles
              const existingContainer: am5.Container | undefined = existingCircleEffect.parent;
              if (existingContainer && !existingContainer.isDisposed()) {
                existingContainer.dispose();
              }
            }

            // Get the color for the current series
            const seriesIndex: number = series.findIndex((s: Series) => s.field === config.field);
            const seriesColor: am5.Color = config.color
              ? am5.color(
                chartConfiguration?.colors?.lineSeriesColor?.[config.color] || config.color,
              )
              : am5.color(defaultSeriesColor[seriesIndex % defaultSeriesColor.length]);

            // Create a container for the bullet with two circles
            const container: am5.Container = am5.Container.new(customRoot, {});

            // Create bullet circle (base circle)
            container.children.push(am5.Circle.new(
              customRoot,
              { radius: 5, fill: seriesColor },
            ));

            // Create effect circle (pulsing effect)
            const circleEffect: am5.Circle = container.children.push(am5.Circle.new(
              customRoot,
              {
                radius: 5,
                fill: seriesColor,
                opacity: 0.5,
              },
            ));

            // Store the circle effect for later animation updates
            activeBulletsRef.current.set(seriesId, circleEffect);

            // Apply initial animation if active
            if (bulletAnimationStateRef.current) {
              setTimeout((): void => {
                if (circleEffect && !circleEffect.isDisposed() && bulletAnimationStateRef.current) {
                  circleEffect.animate({
                    key: 'radius',
                    to: 15,
                    duration: 1000,
                    easing: am5.ease.out(am5.ease.cubic),
                    loops: Infinity,
                  });
                  circleEffect.animate({
                    key: 'opacity',
                    from: 0.8,
                    to: 0,
                    duration: 1000,
                    easing: am5.ease.out(am5.ease.cubic),
                    loops: Infinity,
                  });
                }
              }, 150);
            }

            // Create the bullet element
            const bulletElement: am5.Bullet = am5.Bullet.new(customRoot, {
              sprite: container,
            });

            return bulletElement;
          }

          return undefined;
        });
      }
    });
  }, [
    series,
    chartConfiguration,
    defaultSeriesColor,
    bulletAnimationStateRef,
  ]);

  /* ***********************************************************************************************
  *************************************** COMPONENT HANDLING ***************************************
  *********************************************************************************************** */

  /**
   * Filters and reduces data to the maximum number of data points.
   *
   * @param rawData - The raw input data
   * @returns {IChartDataRecord[]} Filtered and reduced data array
   */
  const processRawChartData = useCallback((rawData: unknown): IChartDataRecord[] => {
    const filteredData: IChartDataRecord[] = Array.isArray(rawData)
      ? (rawData as IChartDataRecord[])
      : [];

    // Reduce the data to the last MAX_DATA_POINTS
    return filteredData.reduce(
      (
        accumulator: IChartDataRecord[],
        item: IChartDataRecord,
        index: number,
        array: IChartDataRecord[],
      ) => {
        if (array.length - index <= MAX_DATA_POINTS) {
          accumulator.push({ ...item });
        }
        return accumulator;
      },
      [],
    );
  }, []);

  /**
   * Processes status series data by mapping status strings to indices.
   *
   * @param inputData - Input data array
   * @param config - Series configuration
   * @returns {IChartDataRecord[]} Processed data with status mapping
   */
  const processStatusSeriesData = useCallback((
    inputData: IChartDataRecord[],
    config: Series,
  ): IChartDataRecord[] => {
    let processedData: IChartDataRecord[] = [];

    if (config.status) {
      const statusMapping: StatusMappingResult = mapStatusArrayToIndex(config.status);
      processedData = inputData.map((item: IChartDataRecord) => {
        // Create a copy of the item
        const updatedItem: IChartDataRecord = { ...item };

        // Update the statusValue field with the mapped value
        const key: keyof IChartDataRecord = config.field as keyof IChartDataRecord;
        const statusKey: string = String(item[key]);
        updatedItem.statusValue = statusMapping[statusKey];

        // Return the updated item
        return updatedItem;
      });
    } else {
      processedData = inputData;
    }

    return processedData;
  }, [mapStatusArrayToIndex]);

  /**
   * Processes measurement series data by applying unit conversions.
   *
   * @param inputData - Input data array
   * @param config - Series configuration
   * @returns {IChartDataRecord[]} Processed data with converted measurements
   */
  const processMeasurementSeriesData = useCallback((
    inputData: IChartDataRecord[],
    config: Series,
  ): IChartDataRecord[] => {
    const processedData: IChartDataRecord[] = inputData
      .map((item: IChartDataRecord) => {
        // Get the original value from the item
        const originalValue: ChartDataScalar = item[config.field];

        // Convert the value based on the measurementType
        const convertedValue: number | null | undefined = convertMeasurementValue(
          originalValue as number,
          config.measurementType,
        );

        // Return the updated item with the converted value
        return { ...item, [config.field]: convertedValue };
      })
      .filter((item: IChartDataRecord) => {
        // Filter out items where the field value is null or undefined
        const value: ChartDataScalar = item[config.field];
        return value !== null && value !== undefined;
      });

    return processedData;
  }, [convertMeasurementValue]);

  /**
   * Sorts chart data by timestamp in ascending order.
   *
   * @param inputData - Input data array
   * @param dateField - The field name containing timestamp data
   * @returns {IChartDataRecord[]} Sorted data array
   */
  const sortChartDataByTimestamp = useCallback((
    inputData: IChartDataRecord[],
    dateField: string,
  ): IChartDataRecord[] => {
    const sortedData: IChartDataRecord[] = [...inputData];

    sortedData.sort((a: IChartDataRecord, b: IChartDataRecord) => {
      const aValue: number = a?.[dateField] as number ?? 0;
      const bValue: number = b?.[dateField] as number ?? 0;
      return aValue - bValue;
    });

    return sortedData;
  }, []);

  /**
   * Processes series data based on its type and configuration.
   *
   * @param inputData - Input data array
   * @param config - Series configuration
   * @returns {IChartDataRecord[]} Processed data array
   */
  const processSeriesData = useCallback((
    inputData: IChartDataRecord[],
    config: Series | undefined,
  ): IChartDataRecord[] => {
    let finalData: IChartDataRecord[] = [];

    if (!config) {
      // If no configuration is found, use the data as-is
      finalData = inputData;
    } else if (config.type === 'status') {
      // For "status" type, map the status values using the provided mapping
      finalData = processStatusSeriesData(inputData, config);
    } else if (config.type === 'fill') {
      // For "fill" type, no conversion is required
      finalData = inputData;
    } else {
      // For other types, convert the value using the measurement converter
      finalData = processMeasurementSeriesData(inputData, config);
    }

    return finalData;
  }, [processStatusSeriesData, processMeasurementSeriesData]);

  /**
   * Updates the Y-axis scale dynamically based on current data.
   * Considers ALL series that share the same unit and measurement type.
   * ALWAYS applies margin when values exceed or equal the configured limits.
   */
  const updateYAxisScale = useCallback((
    yAxis: am5xy.ValueAxis<AxisRenderer>,
    processedData: IChartDataRecord[],
    config: Series | undefined,
  ): void => {
    if (!config || (config.minScale === undefined && config.maxScale === undefined)) {
      return;
    }

    // Ignore special series types (fill, status)
    if (config.type === 'fill' || config.type === 'status') {
      return;
    }

    // Configured values (default)
    const configuredMin: number = convertMeasurementValue(
      config.minScale,
      config.measurementType,
    ) as number;
    const configuredMax: number = convertMeasurementValue(
      config.maxScale,
      config.measurementType,
    ) as number;

    // Find all series that share the same unit and measurement type
    const relatedSeries: Series[] = series.filter((s: Series) => (
      s.unit === config.unit
      && s.measurementType === config.measurementType
      && s.type !== 'fill'
      && s.type !== 'status'
    ));

    // If no data available, use configured values
    if (!processedData || processedData.length === 0) {
      yAxis.set('min', configuredMin);
      yAxis.set('max', configuredMax);
      return;
    }

    // Calculate global min/max considering ALL related series
    const globalMinMax: {
      min: number | undefined;
      max: number | undefined;
    } = calculateGlobalMinMaxForUnit(processedData, relatedSeries);

    // If no valid data, use configured values
    if (globalMinMax.min === undefined || globalMinMax.max === undefined) {
      yAxis.set('min', configuredMin);
      yAxis.set('max', configuredMax);
      return;
    }

    const dataMin: number = globalMinMax.min;
    const dataMax: number = globalMinMax.max;

    // Calculate dynamic margin based on value magnitude
    const dynamicMargin: number = calculateDynamicMargin(
      dataMin,
      dataMax,
      configuredMin,
      configuredMax,
    );

    // Initialize with configured values
    let finalMin: number = configuredMin;
    let finalMax: number = configuredMax;

    // LOGIC FOR MINIMUM VALUE
    if (config.minScale !== undefined) {
      // Case 1: Data below configured min -> extend scale down with margin
      if (dataMin < configuredMin) {
        finalMin = dataMin - dynamicMargin;
      } else if (Math.abs(dataMin - configuredMin) < dynamicMargin * 0.1) {
        // Case 2: Data EQUAL or very close to configured min -> apply margin
        finalMin = dataMin - dynamicMargin;
      } else {
        // Case 3: Data within configured range -> keep configured value
        finalMin = configuredMin;
      }
    } else {
      // If no minScale configured, use data minimum with margin
      finalMin = dataMin - dynamicMargin;
    }

    // LOGIC FOR MAXIMUM VALUE
    if (config.maxScale !== undefined) {
      // Case 1: Data above configured max -> extend scale up with margin
      if (dataMax > configuredMax) {
        finalMax = dataMax + dynamicMargin;
      } else if (Math.abs(dataMax - configuredMax) < dynamicMargin * 0.1) {
        // Case 2: Data EQUAL or very close to configured max -> apply margin
        finalMax = dataMax + dynamicMargin;
      } else {
        // Case 3: Data within configured range -> keep configured value
        finalMax = configuredMax;
      }
    } else {
      // If no maxScale configured, use data maximum with margin
      finalMax = dataMax + dynamicMargin;
    }

    // VALIDATION: Ensure min < max (special case when all values are equal)
    if (finalMin >= finalMax) {
      const adjustment: number = dynamicMargin || 1;
      finalMin -= adjustment;
      finalMax += adjustment;
    }

    // Apply calculated values to Y-axis
    yAxis.set('min', finalMin);
    yAxis.set('max', finalMax);
  }, [
    calculateGlobalMinMaxForUnit,
    calculateDynamicMargin,
    series,
    convertMeasurementValue,
  ]);

  /**
   * Updates the chart series data when input data changes.
   * Handles data processing, conversion, and sorting for all series types.
   */
  const updateChartSeriesData = useCallback((): void => {
    if (chartRef.current && xAxisRef.current) {
      const processedData: IChartDataRecord[] = processRawChartData(data);

      // Update baseInterval before updating series data to ensure correct parsing
      const dateFieldName: string = series?.[0]?.date || 'timestamp';
      // Add a fixed BaseInterval to avoid recalculating it every time in seconds
      const newInterval: BaseIntervalResult = calculateBaseInterval(processedData, dateFieldName);
      xAxisRef.current.set('baseInterval', newInterval);

      // Update date format based on the new data period (only when format changes)
      const newDateFormat: string = determineDateFormatByPeriod(processedData, dateFieldName);
      updateDateFormatAdapter(newDateFormat);

      chartRef.current.series.each((seriesChart: am5xy.XYSeries): void => {
        // Find the configuration for the current series based on its valueYField
        const config: Series | undefined = series.find(
          (seriesConfig: Series) => seriesConfig.field === seriesChart.get('valueYField'),
        );

        // Process the data based on series configuration
        let finalData: IChartDataRecord[] = processSeriesData(processedData, config);

        // Sort data by timestamp
        const dateField: string | undefined = seriesChart.get('valueXField') || config?.date;
        if (dateField) {
          finalData = sortChartDataByTimestamp(finalData, dateField);
        }

        // Set the final processed data for the current series
        seriesChart.data.setAll(finalData);

        // Update Y-axis scale dynamically based on new data.
        // Fill/status series use a fixed 0–1 scale (set by configureSpecialYAxis) and must
        // not have their axis rescaled, otherwise the fill height jumps mid-animation.
        const yAxis: am5xy.ValueAxis<AxisRenderer> = seriesChart.get(
          'yAxis',
        ) as am5xy.ValueAxis<AxisRenderer>;

        const isFixedScaleSeries: boolean = (
          config?.type === 'fill' || config?.type === 'status'
        );

        if (yAxis && config && !isFixedScaleSeries) {
          updateYAxisScale(yAxis, finalData, config);
        }

        // Update minGridDistance for better label spacing
        xAxisRef.current?.get('renderer').set('minGridDistance', 120);
      });
    }
  }, [
    data,
    series,
    processRawChartData,
    calculateBaseInterval,
    determineDateFormatByPeriod,
    updateDateFormatAdapter,
    processSeriesData,
    sortChartDataByTimestamp,
    updateYAxisScale,
  ]);

  // useEffect hook to create the root when the component mounts
  // Also recreates when measurement units change
  useEffect(() => {
    if (!rootRef.current) {
      // useSafeResolution: false → disables AmCharts' forced resolution=1 on iOS
      // By default AmCharts sets resolution=1 on iOS (ignoring devicePixelRatio)
      // to save memory, but this causes blurry text on Retina displays
      rootRef.current = am5.Root.new(id, { useSafeResolution: false });
    }

    return (): void => {
      if (rootRef.current) {
        rootRef.current.dispose();
        rootRef.current = undefined;
        chartRef.current = null;
        xAxisRef.current = null;
      }
    };
  }, [id, i18n.language, pressureUnit, temperatureUnit, lengthUnit]);

  useEffect(() => {
    const container: HTMLDivElement | null = containerRef.current;
    // Only create if root doesn't exist and chart doesn't exist
    if (rootRef.current && !chartRef.current && container) {
      const root: am5.Root = rootRef.current;
      root.setThemes([am5themes_Animated.new(root)]);

      const { paddingLeft, paddingRight } = calculateAxesPadding();
      const chart: am5xy.XYChart = createChart(root, paddingLeft, paddingRight);
      chartRef.current = chart;

      // Use actual data for createXAxis to determine correct date formatting
      const processedData: IChartDataRecord[] = Array.isArray(data)
        ? data as IChartDataRecord[]
        : [];
      const xAxis: am5xy.DateAxis<am5xy.AxisRenderer> = createXAxis(
        root,
        chart,
        processedData,
        series,
      );
      xAxisRef.current = xAxis;

      const yAxes: am5xy.ValueAxis<AxisRenderer>[] = createYAxes(
        root,
        chart,
        series,
        theme,
        getConvertedUnitLabel,
      );

      // Calculate baseInterval to pass to createSeries
      const dateFieldName: string = series?.[0]?.date || 'timestamp';
      const baseInterval: BaseIntervalResult = calculateBaseInterval(processedData, dateFieldName);

      createSeries(
        root,
        chart,
        xAxis,
        yAxes,
        series,
        chartConfiguration,
        getConvertedUnitLabel,
        defaultSeriesColor,
        baseInterval,
      );

      createLegend(root, chart, theme, compactLegend);
      createCursor(root, chart, xAxis);
      createBullets(chart);

      // Responsive rules: adjust fonts/spacing dynamically based on container width.
      // The guard prevents redundant setAll calls when the breakpoint has not changed,
      // which would cause unnecessary layout recalculations during the entrance animation.
      let lastBreakpoint: boolean | undefined;
      chart.onPrivate('width', (width: number | undefined): void => {
        const isSmallScreen: boolean = (width ?? 0) < RESPONSIVE_CHART_BREAKPOINT;
        if (isSmallScreen === lastBreakpoint) return;
        lastBreakpoint = isSmallScreen;

        const rules: typeof CHART_MOBILE_RULES | typeof CHART_DESKTOP_RULES = (
          isSmallScreen ? CHART_MOBILE_RULES : CHART_DESKTOP_RULES
        ) as typeof CHART_MOBILE_RULES | typeof CHART_DESKTOP_RULES;

        // Adjust X axis labels
        xAxis.get('renderer').labels.template.setAll({
          fontSize: rules.xAxis.fontSize,
          paddingTop: rules.xAxis.paddingTop,
        });

        // Adjust X axis minGridDistance
        xAxis.get('renderer').set('minGridDistance', rules.minGridDistance.x);

        // Adjust Y axes labels and unit labels
        chart.yAxes.each((yAxisItem) => {
          yAxisItem.get('renderer').labels.template.setAll({
            fontSize: rules.yAxis.fontSize,
          });

          // Adjust unit labels (identified by width === 17)
          yAxisItem.children.each((child) => {
            if (child instanceof am5.Label && child.get('width') === 17) {
              child.setAll({
                fontSize: rules.unit.fontSize,
                height: rules.unit.height,
              });
            }
          });
        });

        // Adjust chart padding. paddingLeft/paddingRight are owned by calculateAxesPadding
        // and must not be overridden here.
        chart.setAll({
          paddingTop: rules.chart.paddingTop,
          paddingBottom: rules.chart.paddingBottom,
        });

        // Adjust legend labels
        const legendList = chart.children.values.filter(
          (child) => child instanceof am5.Legend,
        );
        legendList.forEach((legendItem) => {
          const legendInstance = legendItem as am5.Legend;
          legendInstance.labels.template.setAll({
            fontSize: rules.legend.fontSize,
            fontWeight: rules.legend.fontWeight,
            oversizedBehavior: 'none',
            maxWidth: undefined,
            paddingLeft: rules.legend.paddingLeft,
          });
          legendInstance.setAll({
            paddingBottom: isSmallScreen ? 0 : 4,
          });
        });
      });
    }
  }, [
    calculateAxesPadding,
    calculateBaseInterval,
    chartConfiguration,
    compactLegend,
    createBullets,
    createChart,
    createCursor,
    createLegend,
    createSeries,
    createXAxis,
    createYAxes,
    data,
    defaultSeriesColor,
    getConvertedUnitLabel,
    i18n.language,
    id,
    series,
    theme,
  ]);

  // Control grid toggle without recreating structures
  useEffect((): void => {
    const root: am5.Root | undefined = rootRef.current;
    if (root) {
      const xAxis: am5xy.DateAxis<AxisRenderer> | null = xAxisRef.current;
      if (xAxis) {
        xAxis.get('renderer').grid.template.setAll({ visible: grid });
      }
    }
  }, [grid, id, pressureUnit, temperatureUnit, lengthUnit]);

  // Effect hook to update the data for the chart series when the data changes
  useEffect((): void => {
    updateChartSeriesData();
  }, [updateChartSeriesData, data]);

  return (
    <Element
      id={id}
      ref={containerRef}
      data-testid={testId}
      className={className}
    />
  );
}

export { LineChart };
